import { Component } from 'react'
import { toast } from 'react-toastify'
import { IClientUID } from '../../../api/clients'
import {
    getClientWifiConfig,
    updateClientWifiConfig,
    IWifiConfig,
    WifiConfigType,
    getClientWifiScanResult,
    IWifiScanResult,
} from '../../../api/0x0001/wificonfig'
import { getString } from '../../../locale'
import Loader from '../../../components/loader'
import classnames from 'classnames'
import WifiList from '../../../components/wifilist'
import PasswordInput from '../../../components/password-input'

type Props = {
    clientId: IClientUID
}

type State = {
    wifiConfig?: Record<keyof IWifiConfig, string>
    updates: Partial<IWifiConfig>
    errors: Partial<Record<keyof IWifiConfig, string>>
    unsaved: boolean
    scanResult?: IWifiScanResult[]
    scan: boolean
}

class WifiConfigTab extends Component<Props, State> {
    state: State = {
        wifiConfig: undefined,
        updates: {},
        errors: {},
        unsaved: false,
        scanResult: undefined,
        scan: false,
    }

    componentDidMount = () => {
        this.loadWifiConfig()
        // this.scan()
    }

    async scan() {
        this.setState({ scan: true, scanResult: undefined }, async () => {
            try {
                const scanResult = (await getClientWifiScanResult(this.props.clientId)).data
                console.log('scanResult', scanResult)
                this.setState({ scanResult })
            } catch (err) {
                console.error(err)
                toast.error((err as Error).message)
            }
        })
    }

    loadWifiConfig = async () => {
        try {
            const wifiConfig = (await getClientWifiConfig(this.props.clientId)).data
            console.log('wificonfig', wifiConfig)
            this.setState({ wifiConfig, updates: {}, errors: {}, unsaved: false })
        } catch (err) {
            toast.error((err as Error).message)
            console.error(err)
        }
    }

    async saveWifiConfig() {
        try {
            const { updates, errors } = this.state
            if (Object.keys(updates).length === 0) {
                return toast.error(getString('settings.no_updates'))
            }
            if (Object.keys(errors).length > 0) {
                return toast.error(getString('settings.fix_errors'))
            }
            await updateClientWifiConfig(
                this.props.clientId,
                Object.entries(updates).map(([key, value]) => ({
                    type: WifiConfigType[key as any] as any,
                    value: value!,
                })),
            )
            await this.loadWifiConfig()
            toast.success(getString('common.success', 'common.saved'))
        } catch (err) {
            toast.error((err as Error).message)
            console.error(err)
        }
    }

    handleChange(name: keyof IWifiConfig, value: string) {
        const { wifiConfig, updates, errors } = this.state
        wifiConfig![name] = value
        updates[name] = value
        delete errors[name]
        this.setState({ wifiConfig, updates, unsaved: true })
    }

    render() {
        if (this.state.wifiConfig === undefined) return <Loader size={14} />
        return (
            <div>
                {this.renderActions()}
                {this.renderCategories()}
            </div>
        )
    }

    renderActions() {
        return (
            <div className="grid grid-cols-4 gap-5">
                {this.renderActionButton(getString('common.save'), this.saveWifiConfig.bind(this))}
                {this.renderActionButton(getString('settings.refresh'), this.loadWifiConfig.bind(this))}
                {this.renderActionButton(getString('common.scan'), this.scan.bind(this))}
                {this.renderUnsavedChanges()}
            </div>
        )
    }

    renderActionButton(label: string, onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void) {
        return (
            <button
                onClick={onClick}
                className="group py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-501"
            >
                {label}
            </button>
        )
    }

    renderUnsavedChanges() {
        if (!this.state.unsaved) return undefined
        return <div className="dark:text-white flex items-center">* {getString('common.unsaved_changes')}</div>
    }

    renderCategories() {
        return (
            <div className="">
                {this.renderStationCategory()}
                {this.renderScanCategory()}
            </div>
        )
    }

    renderCategoryHeader(label: string) {
        return <h3 className="text-2xl dark:text-white font-semibold text-center">{label}</h3>
    }

    renderInputErrorMessage(message: string) {
        return <div className="text-red-500 text-sm">{message}</div>
    }

    //#region Station Category
    renderStationCategory() {
        return (
            <div className="top-5 bg-white dark:bg-gray-800 rounded-xl shadow-md mt-8 p-6">
                {this.renderCategoryHeader(getString('wificonfig.sta_settings'))}
                {this.renderStaSsid()}
                {this.renderStaPass()}
                {this.renderStaWarning()}
            </div>
        )
    }
    renderStaSsid() {
        const { wifiConfig, errors } = this.state
        const error = errors.staSsid !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('wificonfig.sta_ssid')}</label>
                <input
                    className={classnames('pretty-input ltr', error ? 'border-red-500 dark:border-red-500' : 'border-gray-300')}
                    value={wifiConfig!.staSsid}
                    onInput={(e: any) => this.handleChange('staSsid', e.target.value)}
                />
                {error && this.renderInputErrorMessage(errors.staSsid!)}
            </div>
        )
    }
    renderStaPass() {
        const { wifiConfig, errors } = this.state
        const error = errors.staPass !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('wificonfig.sta_pass')}</label>
                <PasswordInput
                    className={classnames('pretty-input ltr', error ? 'border-red-500 dark:border-red-500' : 'border-gray-300')}
                    value={wifiConfig!.staPass}
                    onChange={value => this.handleChange('staPass', value)}
                />
                {error && this.renderInputErrorMessage(errors.staPass!)}
            </div>
        )
    }
    renderStaWarning() {
        return <p className="mt-4 dark:text-white text-xs">{getString('wificonfig.sta_warning')}</p>
    }
    //#endregion

    //#region Station Category
    renderScanCategory() {
        const { scan } = this.state
        if (!scan) return null
        return (
            <div className="top-5 bg-white dark:bg-gray-800 rounded-xl shadow-md mt-8 p-6">
                {this.renderCategoryHeader(getString('wificonfig.scan'))}
                {this.renderWifiList()}
            </div>
        )
    }
    renderWifiList() {
        return (
            <WifiList
                items={this.state.scanResult}
                onSsidChange={ssid => this.handleChange('staSsid', ssid)}
                onPassChange={pass => this.handleChange('staPass', pass || '')}
                password={this.state.wifiConfig?.staPass}
            />
        )
    }
    //#endregion
}

export default WifiConfigTab
